import { useEffect, useMemo, useState } from 'react'
import { Cable } from 'lucide-react'
import {
    COMPONENT_JOB_SOURCE_EXTENSIONS,
    cancelRun,
    createDefaultComponentJobSettings,
    getComponentRunPaths,
    disableSchedule,
    getRunStatus,
    getWorkspaceJobs,
    getComponentJobCompanionPaths,
    getComponentJobRootPath,
    isComponentJobEntrypointPath,
    parseComponentJobConfig,
    resolveComponentJobScheduleCron,
    runServer,
    serializeComponentJobConfig,
    type ComponentJobSettings,
    type ComponentJobServerRunner,
    type ComponentJobScheduleRecord,
    type ComponentRunStatusRecord,
    type ComponentServerEngine,
} from '@secureblob/sdk/componentJobs'
import { useEngineManager, useFileSystem, useRegistry, useWorkspaceMetadata } from '../hooks/useWorkspace'
import { formatExactTime, timeAgo } from '../utils/writerUtils'
import { useToast } from './Toast'
import { createComponentJobsClientOptions, createComponentRunnerBootstrap } from './componentJobs/apiClient'
import {
    createComponentJobRunSummaryFromStatusRecord,
    getComponentJobSourcePaths,
    isQueuedComponentJobStatus,
    isRunningComponentJobStatus,
    loadComponentJobBuildSummary,
    loadComponentJobRunSummary,
    shortComponentJobArtifactId,
    type ComponentJobBuildSummary,
    type ComponentJobRunSummary,
} from './componentJobs/jobHistory'
import { materializeRunStatusToWorkspace } from './componentJobs/controlPlaneMaterialization'
import {
    hasComponentJobCancelRequest,
    hasComponentJobRunRequest,
    writeComponentJobCancelRequest,
    writeComponentJobRunRequest,
} from './componentJobs/jobControl'
import { ensureComponentJobServerRunner } from './componentJobs/serverRunner'

interface ServerJobsPanelProps {
    onOpenFile?: (path: string) => void
    canWrite?: boolean
    workspaceLabel?: string
}

type ServerJobBucket = 'running' | 'queued' | 'scheduled' | 'recently-finished'

interface ServerJobItem {
    sourcePath: string
    sourceName: string
    settingsPath: string
    settings: ComponentJobSettings
    serverRunner: ComponentJobServerRunner | null
    scheduledArtifactId: string | null
    latestServerRun: ComponentJobRunSummary | null
    latestBuild: ComponentJobBuildSummary | null
    bucket: ServerJobBucket
    bucketLabel: string
    artifactLabel: string | null
    engineLabel: string | null
    timingLabel: string
    summary: string
    cancelRequested: boolean
    runRequested: boolean
}

interface LocalServerJobState {
    settings: ComponentJobSettings
    serverRunner: ComponentJobServerRunner | null
    serverRuns: ComponentJobRunSummary[]
    builds: ComponentJobBuildSummary[]
}

const BUCKET_TITLES: Record<ServerJobBucket, string> = {
    running: 'Running',
    queued: 'Queued',
    scheduled: 'Scheduled',
    'recently-finished': 'Recently finished',
}

function formatScheduleSummary(settings: ComponentJobSettings): string {
    if (!settings.enabled || settings.schedulePreset === 'manual') {
        return 'Manual only'
    }
    if (settings.schedulePreset === 'custom') {
        return settings.cron ? `Custom cron · ${settings.cron}` : 'Custom cron'
    }
    switch (settings.schedulePreset) {
        case 'every-5-min':
            return 'Every 5 min'
        case 'hourly':
            return 'Hourly'
        case 'daily':
            return 'Daily'
        default:
            return 'Scheduled'
    }
}

function resolveServerEngine(enginePolicy: ComponentJobSettings['enginePolicy']): ComponentServerEngine | 'auto' {
    switch (enginePolicy) {
        case 'prefer-wasmtime':
            return 'wasmtime'
        default:
            return 'auto'
    }
}

function isNotFoundComponentJobsError(error: unknown): boolean {
    return typeof error === 'object'
        && error !== null
        && 'code' in error
        && (error as { code?: string }).code === 'NOT_FOUND'
}

function createSettingsFromScheduleRecord(schedule: ComponentJobScheduleRecord): ComponentJobSettings {
    return {
        enabled: schedule.enabled,
        target: 'server',
        schedulePreset: schedule.schedulePreset,
        cron: schedule.schedulePreset === 'custom' ? schedule.cron : '',
        timezone: schedule.timezone || 'UTC',
        payloadPreset: schedule.payloadPreset ?? '',
        enginePolicy: schedule.enginePolicy,
        concurrencyPolicy: schedule.concurrencyPolicy,
    }
}

function mergeLatestServerRun(
    sourcePath: string,
    localRuns: readonly ComponentJobRunSummary[],
    remoteRuns: readonly ComponentRunStatusRecord[],
): ComponentJobRunSummary | null {
    const merged = new Map<string, ComponentJobRunSummary>()

    for (const run of localRuns) {
        merged.set(run.runId, run)
    }

    for (const run of remoteRuns) {
        const metadataPath = merged.get(run.runId)?.metadataPath ?? getComponentRunPaths(sourcePath, run.runId).metadata
        merged.set(run.runId, createComponentJobRunSummaryFromStatusRecord(run, metadataPath))
    }

    return [...merged.values()]
        .sort((left, right) => {
            if (right.startedAt !== left.startedAt) return right.startedAt - left.startedAt
            return left.runId.localeCompare(right.runId)
        })[0] ?? null
}

function inferScheduledEngineLabel(settings: ComponentJobSettings): string | null {
    switch (settings.enginePolicy) {
        case 'prefer-wasmtime':
            return 'wasmtime'
        default:
            return null
    }
}

function resolveDisplayedArtifactId(
    bucket: ServerJobBucket,
    scheduledArtifactId: string | null,
    latestServerRun: ComponentJobRunSummary | null,
    latestBuild: ComponentJobBuildSummary | null,
): string | null {
    if (bucket === 'scheduled' && scheduledArtifactId) {
        return scheduledArtifactId
    }

    return latestServerRun?.artifactId ?? latestBuild?.artifactId ?? scheduledArtifactId ?? null
}

function resolveRunNowArtifactId(job: Pick<ServerJobItem, 'settings' | 'scheduledArtifactId' | 'latestServerRun' | 'latestBuild'>): string | null {
    if (job.settings.enabled && job.scheduledArtifactId) {
        return job.scheduledArtifactId
    }

    return job.latestServerRun?.artifactId ?? job.latestBuild?.artifactId ?? job.scheduledArtifactId
}

function getServerJobDisplayName(sourcePath: string): string {
    const jobRoot = getComponentJobRootPath(sourcePath)
    if (jobRoot) {
        const rootName = jobRoot.split('/').pop()
        if (rootName) {
            return rootName.replace(/\.job$/i, '')
        }
    }

    return sourcePath.split('/').pop() ?? sourcePath
}

function buildServerJobItem(
    sourcePath: string,
    settings: ComponentJobSettings,
    serverRunner: ComponentJobServerRunner | null,
    scheduledArtifactId: string | null,
    latestServerRun: ComponentJobRunSummary | null,
    latestBuild: ComponentJobBuildSummary | null,
    fileSet: ReadonlySet<string>,
): ServerJobItem | null {
    let bucket: ServerJobBucket | null = null

    if (latestServerRun && isRunningComponentJobStatus(latestServerRun.status)) {
        bucket = 'running'
    } else if (latestServerRun && isQueuedComponentJobStatus(latestServerRun.status)) {
        bucket = 'queued'
    } else if (settings.enabled) {
        bucket = 'scheduled'
    } else if (latestServerRun) {
        bucket = 'recently-finished'
    }

    if (!bucket) return null

    const sourceName = getServerJobDisplayName(sourcePath)
    const { settings: settingsPath } = getComponentJobCompanionPaths(sourcePath)
    const artifactId = resolveDisplayedArtifactId(bucket, scheduledArtifactId, latestServerRun, latestBuild)
    const artifactLabel = artifactId ? `artifact ${shortComponentJobArtifactId(artifactId)}` : null
    const engineLabel = latestServerRun?.engine ?? latestBuild?.recommendedServerEngine ?? inferScheduledEngineLabel(settings)
    const scheduleSummary = formatScheduleSummary(settings)
    const resolvedCron = resolveComponentJobScheduleCron(settings)
    const cancelRequested = latestServerRun
        ? hasComponentJobCancelRequest(fileSet, sourcePath, latestServerRun.runId)
        : false
    const runRequested = hasComponentJobRunRequest(fileSet, sourcePath, 'server')

    let timingLabel = 'Awaiting server-side history'
    if (bucket === 'scheduled') {
        if (latestServerRun?.nextRunAtLabel) {
            timingLabel = `Next run ${latestServerRun.nextRunAtLabel}`
        } else if (resolvedCron) {
            timingLabel = `${scheduleSummary} · ${settings.timezone}`
        } else {
            timingLabel = `Scheduled · ${settings.timezone}`
        }
    } else if (latestServerRun) {
        timingLabel = `${latestServerRun.startedAtLabel} · ${timeAgo(latestServerRun.startedAt, { maxDepth: 'days' })}`
    }

    let summary = 'Awaiting server-side history'
    if (bucket === 'scheduled') {
        if (latestServerRun?.summary) {
            summary = latestServerRun.summary
        } else if (latestServerRun) {
            summary = `Latest run ${latestServerRun.statusLabel} · ${latestServerRun.startedAtLabel}`
        } else if (resolvedCron) {
            summary = `Cron ${resolvedCron}`
        } else {
            summary = 'Schedule is enabled for future server runs'
        }
    } else if (latestServerRun?.summary) {
        summary = latestServerRun.summary
    } else if (latestBuild) {
        summary = `${latestBuild.statusLabel} build materialized ${timeAgo(latestBuild.createdAt, { maxDepth: 'days' })}`
    }

    return {
        sourcePath,
        sourceName,
        settingsPath,
        settings,
        serverRunner,
        scheduledArtifactId,
        latestServerRun,
        latestBuild,
        bucket,
        bucketLabel: BUCKET_TITLES[bucket],
        artifactLabel,
        engineLabel,
        timingLabel,
        summary,
        cancelRequested,
        runRequested,
    }
}

function mergeRunStatusIntoJob(
    job: ServerJobItem,
    status: ComponentRunStatusRecord,
    fileSet: ReadonlySet<string>,
): ServerJobItem | null {
    const sourcePath = status.sourcePath ?? job.sourcePath
    const metadataPath = job.latestServerRun?.runId === status.runId
        ? job.latestServerRun.metadataPath
        : getComponentRunPaths(sourcePath, status.runId).metadata

    return buildServerJobItem(
        sourcePath,
        job.settings,
        job.serverRunner,
        job.scheduledArtifactId,
        createComponentJobRunSummaryFromStatusRecord(status, metadataPath),
        job.latestBuild,
        fileSet,
    )
}

export default function ServerJobsPanel({
    onOpenFile,
    canWrite = true,
    workspaceLabel,
}: ServerJobsPanelProps) {
    const engineManager = useEngineManager()
    const { files, readFile, writeFile, mkdir } = useFileSystem()
    const { entries } = useRegistry()
    const { blobId: workspaceBlobId } = useWorkspaceMetadata()
    const toast = useToast()
    const [jobs, setJobs] = useState<ServerJobItem[]>([])
    const [openingRunSourcePath, setOpeningRunSourcePath] = useState<string | null>(null)

    const sourcePaths = useMemo(() => getComponentJobSourcePaths(files), [files])
    const fileSet = useMemo(() => new Set(files.filter(file => file.kind === 'file').map(file => file.path)), [files])
    const currentWorkspaceEntry = useMemo(
        () => entries.find(entry => entry.blobId === workspaceBlobId) ?? null,
        [entries, workspaceBlobId],
    )
    const componentJobsClient = useMemo(
        () => (currentWorkspaceEntry ? createComponentJobsClientOptions(currentWorkspaceEntry) : null),
        [currentWorkspaceEntry],
    )
    const workspaceWriter = useMemo(() => ({
        writeText: (targetPath: string, content: string) => writeFile(targetPath, content),
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        writeBytes: (_targetPath: string, _content: Uint8Array) => {
            throw new Error('ServerJobsPanel only materializes text run artifacts.')
        },
    }), [writeFile])

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            const localStates = new Map<string, LocalServerJobState>()

            await Promise.all(sourcePaths.map(async sourcePath => {
                const { settings: settingsPath, runsDir, buildsDir } = getComponentJobCompanionPaths(sourcePath)
                const config = parseComponentJobConfig(await readFile(settingsPath))

                const runMetadataFiles = files
                    .filter(file => file.kind === 'file' && file.path.startsWith(runsDir + '/') && file.path.endsWith('/run.json'))
                    .sort((left, right) => right.mtime - left.mtime)

                const buildManifestFiles = files
                    .filter(file => file.kind === 'file' && file.path.startsWith(buildsDir + '/') && file.path.endsWith('/manifest.json'))
                    .sort((left, right) => right.mtime - left.mtime)

                const serverRuns = (await Promise.all(
                    runMetadataFiles.map(file => loadComponentJobRunSummary(file, readFile)),
                ))
                    .filter((item): item is ComponentJobRunSummary => item !== null && item.target === 'server')
                    .sort((left, right) => right.startedAt - left.startedAt)

                const builds = (await Promise.all(
                    buildManifestFiles.map(file => loadComponentJobBuildSummary(file, readFile)),
                ))
                    .filter((item): item is ComponentJobBuildSummary => item !== null)
                    .sort((left, right) => right.createdAt - left.createdAt)

                localStates.set(sourcePath, {
                    settings: config.schedule,
                    serverRunner: config.serverRunner ?? null,
                    serverRuns,
                    builds,
                })
            }))

            let remoteWorkspaceJobs: Awaited<ReturnType<typeof getWorkspaceJobs>> | null = null
            if (componentJobsClient) {
                try {
                    remoteWorkspaceJobs = await getWorkspaceJobs({
                        ...componentJobsClient,
                        workspaceBlobId,
                    })
                } catch {
                    remoteWorkspaceJobs = null
                }
            }

            const remoteSchedulesBySource = new Map<string, ComponentJobScheduleRecord>()
            const remoteRunsBySource = new Map<string, ComponentRunStatusRecord[]>()

            for (const schedule of remoteWorkspaceJobs?.schedules ?? []) {
                if (!isComponentJobEntrypointPath(schedule.sourcePath)) {
                    continue
                }
                remoteSchedulesBySource.set(schedule.sourcePath, schedule)
            }

            for (const run of remoteWorkspaceJobs?.runs ?? []) {
                if (!run.sourcePath || !isComponentJobEntrypointPath(run.sourcePath)) {
                    continue
                }

                const current = remoteRunsBySource.get(run.sourcePath) ?? []
                current.push(run)
                remoteRunsBySource.set(run.sourcePath, current)
            }

            // Remap remote schedules/runs to local source paths when they share the same
            // job root (folder-first) or the same path-without-extension (source-first)
            // but differ in extension (e.g. server stored deploy.ts, local has deploy.tsx).
            // Without this, deduplication keeps the local item but silently drops the remote schedule
            // or shows duplicate cards for the same conceptual job.
            const localRootMap = new Map<string, string>()
            for (const sp of sourcePaths) {
                const root = getComponentJobRootPath(sp)
                if (root) {
                    localRootMap.set(root, sp)
                } else {
                    // Source-first (file-backed) job: map by path without extension
                    const ext = COMPONENT_JOB_SOURCE_EXTENSIONS.find(e => sp.endsWith(e))
                    if (ext) localRootMap.set(sp.slice(0, -ext.length), sp)
                }
            }

            // Build a cross-reference map for remote-only paths so that even when there
            // is no local file (e.g. on-demand sync workspace), remote schedule and run
            // paths that refer to the same conceptual job are merged under one
            // canonical source path.
            const remoteCanonicalMap = new Map<string, string>()
            for (const remotePath of [...remoteSchedulesBySource.keys(), ...remoteRunsBySource.keys()]) {
                const root = getComponentJobRootPath(remotePath)
                if (root) {
                    if (!remoteCanonicalMap.has(root)) {
                        remoteCanonicalMap.set(root, remotePath)
                    }
                    continue
                }

                const ext = COMPONENT_JOB_SOURCE_EXTENSIONS.find(e => remotePath.endsWith(e))
                if (!ext) continue
                const stem = remotePath.slice(0, -ext.length)
                if (!remoteCanonicalMap.has(stem)) {
                    remoteCanonicalMap.set(stem, remotePath)
                }
            }

            function remapToLocalSourcePath(remotePath: string): string {
                // Folder-first: match by .job root
                const remoteRoot = getComponentJobRootPath(remotePath)
                if (remoteRoot) {
                    const localPath = localRootMap.get(remoteRoot)
                    if (localPath && localPath !== remotePath) return localPath
                    const canonicalPath = remoteCanonicalMap.get(remoteRoot)
                    if (canonicalPath && canonicalPath !== remotePath) return canonicalPath
                } else {
                    // Source-first: match by path without extension
                    const ext = COMPONENT_JOB_SOURCE_EXTENSIONS.find(e => remotePath.endsWith(e))
                    if (ext) {
                        const stem = remotePath.slice(0, -ext.length)
                        // First try local map
                        const localPath = localRootMap.get(stem)
                        if (localPath && localPath !== remotePath) return localPath
                        // Then try remote canonical map
                        const canonicalPath = remoteCanonicalMap.get(stem)
                        if (canonicalPath && canonicalPath !== remotePath) return canonicalPath
                    }
                }
                return remotePath
            }

            const resolvedSchedulesBySource = new Map<string, ComponentJobScheduleRecord>()
            for (const [remotePath, schedule] of remoteSchedulesBySource) {
                const localPath = remapToLocalSourcePath(remotePath)
                resolvedSchedulesBySource.set(localPath, schedule)
            }

            const resolvedRunsBySource = new Map<string, ComponentRunStatusRecord[]>()
            for (const [remotePath, runs] of remoteRunsBySource) {
                const localPath = remapToLocalSourcePath(remotePath)
                const existing = resolvedRunsBySource.get(localPath) ?? []
                resolvedRunsBySource.set(localPath, [...existing, ...runs])
            }

            const candidateSourcePaths = new Set<string>([
                ...sourcePaths,
                ...resolvedSchedulesBySource.keys(),
                ...resolvedRunsBySource.keys(),
            ])

            const nextJobs = [...candidateSourcePaths].map(sourcePath => {
                const localState = localStates.get(sourcePath) ?? {
                    settings: createDefaultComponentJobSettings(),
                    serverRunner: null,
                    serverRuns: [],
                    builds: [],
                }
                const remoteSchedule = resolvedSchedulesBySource.get(sourcePath) ?? null
                const settings = remoteSchedule
                    ? createSettingsFromScheduleRecord(remoteSchedule)
                    : localState.settings
                const latestServerRun = mergeLatestServerRun(
                    sourcePath,
                    localState.serverRuns,
                    resolvedRunsBySource.get(sourcePath) ?? [],
                )

                return buildServerJobItem(
                    sourcePath,
                    settings,
                    localState.serverRunner,
                    remoteSchedule?.artifactId ?? null,
                    latestServerRun,
                    localState.builds[0] ?? null,
                    fileSet,
                )
            })

            if (cancelled) return

            // Deduplicate by job root (folder-first) or by stem (source-first):
            // if both main.ts and main.tsx (or other variants) somehow remain as
            // separate candidates, keep only the first (canonical) one per root/stem.
            const dedupedJobs: ServerJobItem[] = []
            const seenRoots = new Set<string>()
            for (const job of nextJobs.filter((item): item is ServerJobItem => item !== null)) {
                const root = getComponentJobRootPath(job.sourcePath)
                if (root) {
                    // Folder-first: deduplicate by .job root
                    if (seenRoots.has(root)) continue
                    seenRoots.add(root)
                } else {
                    // Source-first: deduplicate by stem (path without extension)
                    const ext = COMPONENT_JOB_SOURCE_EXTENSIONS.find(e => job.sourcePath.endsWith(e))
                    const stem = ext ? job.sourcePath.slice(0, -ext.length) : job.sourcePath
                    if (seenRoots.has(stem)) continue
                    seenRoots.add(stem)
                }
                dedupedJobs.push(job)
            }

            setJobs(dedupedJobs)
        }

        load().catch(() => {
            if (!cancelled) {
                setJobs([])
            }
        })

        return () => {
            cancelled = true
        }
    }, [componentJobsClient, fileSet, files, readFile, sourcePaths, workspaceBlobId])

    const jobsByBucket = useMemo(() => {
        const groups: Record<ServerJobBucket, ServerJobItem[]> = {
            running: [],
            queued: [],
            scheduled: [],
            'recently-finished': [],
        }

        for (const job of jobs) {
            groups[job.bucket].push(job)
        }

        for (const bucket of Object.keys(groups) as ServerJobBucket[]) {
            groups[bucket].sort((left, right) => {
                const leftTime = left.latestServerRun?.startedAt ?? left.latestBuild?.createdAt ?? 0
                const rightTime = right.latestServerRun?.startedAt ?? right.latestBuild?.createdAt ?? 0
                if (rightTime !== leftTime) return rightTime - leftTime
                return left.sourcePath.localeCompare(right.sourcePath)
            })
        }

        return groups
    }, [jobs])

    const materializeServerRun = async (sourcePath: string, status: ComponentRunStatusRecord) => {
        await materializeRunStatusToWorkspace({
            sourcePath,
            status,
            startedAt: status.startedAt,
            eventHistory: [{
                stage: status.stage,
                message: status.message,
                updatedAt: status.updatedAt,
                totalElapsedSeconds: status.totalElapsedSeconds,
                stageElapsedSeconds: status.stageElapsedSeconds,
                error: status.error,
            }],
            workspace: workspaceWriter,
            client: componentJobsClient,
        })
    }

    const ensureJobRunnerBootstrap = async (job: ServerJobItem) => {
        if (!currentWorkspaceEntry) {
            throw new Error('Component job server-runner setup is unavailable for the current workspace.')
        }

        const resolvedServerRunner = await ensureComponentJobServerRunner({
            engine: engineManager,
            workspaceEntry: currentWorkspaceEntry,
            sourceName: job.sourceName,
            existingServerRunner: job.serverRunner,
        })

        if (
            !job.serverRunner
            || job.serverRunner.configBlobId !== resolvedServerRunner.configBlobId
            || job.serverRunner.connectionCredential !== resolvedServerRunner.connectionCredential
            || job.serverRunner.label !== resolvedServerRunner.label
        ) {
            await writeFile(
                job.settingsPath,
                `${serializeComponentJobConfig({
                    schedule: job.settings,
                    serverRunner: resolvedServerRunner,
                })}\n`,
            )

            setJobs(current => current.map(candidate => candidate.sourcePath === job.sourcePath
                ? { ...candidate, serverRunner: resolvedServerRunner }
                : candidate))
        }

        return createComponentRunnerBootstrap(currentWorkspaceEntry, resolvedServerRunner)
    }

    const handleDisableSchedule = async (job: ServerJobItem) => {
        const nextSettings: ComponentJobSettings = { ...job.settings, enabled: false }
        try {
            if (componentJobsClient) {
                try {
                    await disableSchedule({
                        ...componentJobsClient,
                        sourcePath: job.sourcePath,
                        workspaceBlobId,
                    })
                } catch (error) {
                    if (!isNotFoundComponentJobsError(error)) {
                        throw error
                    }
                }
            }

            await writeFile(
                job.settingsPath,
                `${serializeComponentJobConfig({
                    schedule: nextSettings,
                    ...(job.serverRunner ? { serverRunner: job.serverRunner } : {}),
                })}\n`,
            )
            setJobs(current => current
                .map(candidate => {
                    if (candidate.sourcePath !== job.sourcePath) {
                        return candidate
                    }

                    const next = buildServerJobItem(
                        candidate.sourcePath,
                        nextSettings,
                        candidate.serverRunner,
                        candidate.scheduledArtifactId,
                        candidate.latestServerRun,
                        candidate.latestBuild,
                        fileSet,
                    )

                    return next
                        ? {
                            ...next,
                            cancelRequested: candidate.cancelRequested,
                            runRequested: candidate.runRequested,
                        }
                        : null
                })
                .filter((candidate): candidate is ServerJobItem => candidate !== null))
            toast(`Disabled schedule for ${job.sourceName}`, { type: 'success' })
        } catch (error) {
            toast(
                error instanceof Error
                    ? `Failed to disable schedule: ${error.message}`
                    : 'Failed to disable schedule',
                { type: 'error' },
            )
        }
    }

    const handleCancelRun = async (job: ServerJobItem) => {
        if (!job.latestServerRun) return

        try {
            if (componentJobsClient) {
                const cancelled = await cancelRun({
                    ...componentJobsClient,
                    runId: job.latestServerRun.runId,
                })
                setJobs(current => current
                    .map(candidate => candidate.sourcePath === job.sourcePath
                        ? mergeRunStatusIntoJob(candidate, cancelled, fileSet)
                        : candidate)
                    .filter((candidate): candidate is ServerJobItem => candidate !== null))
                void materializeServerRun(cancelled.sourcePath ?? job.sourcePath, cancelled)
            } else {
                writeComponentJobCancelRequest(job.sourcePath, job.latestServerRun.runId, { writeFile, mkdir })

                setJobs(current => current.map(candidate => candidate.sourcePath === job.sourcePath
                    ? { ...candidate, cancelRequested: true }
                    : candidate))
            }
            toast(`Cancel requested for ${job.sourceName}`, { type: 'success' })
        } catch (error) {
            toast(
                error instanceof Error
                    ? `Failed to request cancel: ${error.message}`
                    : 'Failed to request cancel',
                { type: 'error' },
            )
        }
    }

    const handleRunNow = async (job: ServerJobItem) => {
        try {
            if (componentJobsClient) {
                const runnerBootstrap = await ensureJobRunnerBootstrap(job)
                const artifactId = resolveRunNowArtifactId(job)
                if (!artifactId) {
                    throw new Error('No materialized artifact is available for this job yet.')
                }

                const started = await runServer({
                    ...componentJobsClient,
                    artifactId,
                    workspaceBlobId,
                    engine: resolveServerEngine(job.settings.enginePolicy),
                    runInput: job.settings.payloadPreset || undefined,
                    sourcePath: job.sourcePath,
                    runnerBootstrap,
                })
                setJobs(current => current
                    .map(candidate => candidate.sourcePath === job.sourcePath
                        ? mergeRunStatusIntoJob(candidate, started, fileSet)
                        : candidate)
                    .filter((candidate): candidate is ServerJobItem => candidate !== null))
                void materializeServerRun(started.sourcePath ?? job.sourcePath, started)
            } else {
                writeComponentJobRunRequest(job.sourcePath, 'server', { writeFile, mkdir })

                setJobs(current => current.map(candidate => candidate.sourcePath === job.sourcePath
                    ? { ...candidate, runRequested: true }
                    : candidate))
            }
            toast(`Requested server run for ${job.sourceName}`, { type: 'success' })
        } catch (error) {
            toast(
                error instanceof Error
                    ? `Failed to request server run: ${error.message}`
                    : 'Failed to request server run',
                { type: 'error' },
            )
        }
    }

    const handleOpenLatestRun = async (job: ServerJobItem) => {
        if (!job.latestServerRun || !onOpenFile) {
            return
        }

        const metadataPath = job.latestServerRun.metadataPath
        if (fileSet.has(metadataPath)) {
            onOpenFile(metadataPath)
            return
        }

        if (!componentJobsClient) {
            toast('Latest run has not been materialized into this workspace yet.', { type: 'error' })
            return
        }

        setOpeningRunSourcePath(job.sourcePath)
        try {
            const status = await getRunStatus({
                ...componentJobsClient,
                runId: job.latestServerRun.runId,
            })
            const sourcePath = status.sourcePath ?? job.sourcePath
            await materializeServerRun(sourcePath, status)
            setJobs(current => current
                .map(candidate => candidate.sourcePath === job.sourcePath
                    ? mergeRunStatusIntoJob(candidate, status, fileSet)
                    : candidate)
                .filter((candidate): candidate is ServerJobItem => candidate !== null))
            onOpenFile(getComponentRunPaths(sourcePath, status.runId).metadata)
        } catch (error) {
            toast(
                error instanceof Error
                    ? `Failed to open latest run: ${error.message}`
                    : 'Failed to open latest run',
                { type: 'error' },
            )
        } finally {
            setOpeningRunSourcePath(current => current === job.sourcePath ? null : current)
        }
    }

    return (
        <section className="ws-settings-section">
            <div className="ws-settings-section-header">
                <span className="ws-settings-section-title">
                    <Cable size={13} /> Automation
                    <span className="ws-settings-section-badge">{jobs.length}</span>
                </span>
            </div>

            <p className="ws-server-jobs-copy">
                Server Jobs groups running, queued, scheduled, and recent server-side component jobs from the current workspace.
            </p>

            {jobs.length === 0 ? (
                <div className="ws-settings-empty">
                    No scheduled or server-run component jobs have been materialized yet.
                </div>
            ) : (
                <div className="ws-server-jobs-groups">
                    {(Object.keys(BUCKET_TITLES) as ServerJobBucket[]).map(bucket => {
                        const bucketJobs = jobsByBucket[bucket]
                        if (bucketJobs.length === 0) return null

                        return (
                            <section key={bucket} className="ws-server-jobs-group">
                                <div className="ws-server-jobs-group-header">
                                    <h3>{BUCKET_TITLES[bucket]}</h3>
                                    <span>{bucketJobs.length}</span>
                                </div>

                                <div className="ws-server-jobs-list">
                                    {bucketJobs.map(job => {
                                        const latestRunPath = job.latestServerRun?.metadataPath ?? null
                                        const canOpenLatestRun = latestRunPath !== null && (fileSet.has(latestRunPath) || componentJobsClient !== null)
                                        const isOpeningLatestRun = openingRunSourcePath === job.sourcePath

                                        return (
                                            <article key={job.sourcePath} className={`ws-server-job-card ${job.bucket}`}>
                                                <div className="ws-server-job-header">
                                                    <div className="ws-server-job-copy">
                                                        <strong>{job.sourceName}</strong>
                                                        <span className="ws-server-job-path">{job.sourcePath}</span>
                                                    </div>
                                                    <span className={`ws-server-job-status ${job.bucket}`}>
                                                        {job.bucketLabel}
                                                    </span>
                                                </div>

                                                <div className="ws-server-job-meta">
                                                    {job.artifactLabel && <span>{job.artifactLabel}</span>}
                                                    {workspaceLabel && <span>{workspaceLabel}</span>}
                                                    {job.engineLabel && <span>{job.engineLabel}</span>}
                                                    <span>{job.timingLabel}</span>
                                                </div>

                                                <p className="ws-server-job-summary">{job.summary}</p>

                                                {job.latestServerRun && (
                                                    <p className="ws-server-job-run-meta">
                                                        <span>{job.latestServerRun.statusLabel}</span>
                                                        <span title={formatExactTime(job.latestServerRun.startedAt)}>
                                                            {job.latestServerRun.startedAtLabel}
                                                        </span>
                                                    </p>
                                                )}

                                                <div className="ws-server-job-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm"
                                                        onClick={() => onOpenFile?.(job.sourcePath)}
                                                        disabled={!onOpenFile}
                                                    >
                                                        Open source
                                                    </button>
                                                    {job.latestServerRun && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm"
                                                            onClick={() => { void handleOpenLatestRun(job) }}
                                                            disabled={!onOpenFile || !canOpenLatestRun || isOpeningLatestRun}
                                                        >
                                                            {isOpeningLatestRun ? 'Opening…' : 'Open latest run'}
                                                        </button>
                                                    )}
                                                    {job.latestServerRun && (job.bucket === 'running' || job.bucket === 'queued') && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm"
                                                            onClick={() => { void handleCancelRun(job) }}
                                                            disabled={!canWrite || job.cancelRequested}
                                                        >
                                                            {job.cancelRequested ? 'Cancel requested' : 'Cancel run'}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm"
                                                        onClick={() => { void handleRunNow(job) }}
                                                        disabled={!canWrite || job.runRequested}
                                                    >
                                                        {job.runRequested ? 'Run requested' : 'Run now'}
                                                    </button>
                                                    {job.settings.enabled && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm"
                                                            onClick={() => { void handleDisableSchedule(job) }}
                                                            disabled={!canWrite}
                                                        >
                                                            Disable schedule
                                                        </button>
                                                    )}
                                                </div>
                                            </article>
                                        )
                                    })}
                                </div>
                            </section>
                        )
                    })}
                </div>
            )}
        </section>
    )
}
